import React, { useEffect, useState } from "react";
import axios from "axios";
import { motion } from "framer-motion";
import { API, getOrCreateUserId } from "../lib/api";
import { getInitials, colorFromName } from "../lib/avatar";

/**
 * Friends Wall — slots into the Profile page below My Cosmetics.
 *
 * Aggregates two backend endpoints:
 *   GET /api/profile/{uid}/h2h           → top rivals from CASUAL match history
 *   GET /api/iou/me?user_id={uid}        → outstanding IOU tally per counterparty
 *
 * Renders:
 *   • Up to 6 rival cards (sorted by total casual games played)
 *   • Each card: avatar, name, W-L record, last-played
 *   • IOU tally rows: "they owe you: 🍦×2" / "you owe: ☕×1"
 *   • Tasteful empty-state when no friend data yet
 *
 * Side effect: nudges D7 retention by giving social players a daily reason
 * to log in ("settle the score with Alex"). Pure UX win — no mutations.
 */
export default function FriendsWall() {
  const [rivals, setRivals] = useState([]);
  const [iou, setIou] = useState({ owed_to_me: {}, i_owe: {} });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    const uid = getOrCreateUserId();
    let cancelled = false;
    (async () => {
      try {
        const [r1, r2] = await Promise.all([
          axios.get(`${API}/profile/${uid}/h2h?limit=6`),
          axios.get(`${API}/iou/me`, { params: { user_id: uid, limit: 60 } }),
        ]);
        if (cancelled) return;
        setRivals(r1.data?.rivals || []);
        setIou({
          owed_to_me: r2.data?.owed_to_me || {},
          i_owe: r2.data?.i_owe || {},
        });
      } catch (e) {
        // Silent fail — Friends Wall is enrichment, not critical
        console.warn("FriendsWall load failed:", e);
      }
      if (!cancelled) setLoading(false);
    })();
    return () => { cancelled = true; };
  }, []);

  const counterparties = mergeCounterparties(rivals, iou);

  // Skip rendering entirely while loading the very first time so we don't
  // flash an "empty" state.
  if (loading) return null;

  // Empty state — invite users to play casual matches.
  if (counterparties.length === 0) {
    return (
      <section className="mb-6" data-testid="friends-wall">
        <div className="flex items-end justify-between mb-3">
          <h2 className="font-display font-black text-xl flex items-center gap-2">
            <span>👥</span> Friends Wall
          </h2>
        </div>
        <div className="glass rounded-3xl p-6 text-center" data-testid="friends-wall-empty">
          <div style={{ fontSize: 32 }} className="mb-2">🤝</div>
          <div className="text-white/85 font-display font-bold mb-1">
            No friend matches yet
          </div>
          <p className="text-white/55 text-sm max-w-sm mx-auto">
            Play a Friend Match (casual) and your head-to-head record will
            appear here. IOU wagers pile up too — perfect for settling scores.
          </p>
        </div>
      </section>
    );
  }

  return (
    <section className="mb-6" data-testid="friends-wall">
      <div className="flex items-end justify-between mb-3">
        <h2 className="font-display font-black text-xl flex items-center gap-2">
          <span>👥</span> Friends Wall
        </h2>
        <div className="text-white/45 text-xs">Casual matches & IOUs</div>
      </div>
      <div className="grid grid-cols-1 md:grid-cols-2 gap-3">
        {counterparties.map((c, idx) => (
          <RivalCard key={c.user_id} entry={c} index={idx} />
        ))}
      </div>
    </section>
  );
}

// ---------- helpers ----------

function mergeCounterparties(rivals, iou) {
  const map = new Map();
  // Seed with rival entries (ensures stable order by games-played)
  for (const r of rivals) {
    map.set(r.user_id, {
      user_id: r.user_id,
      name: r.name || "Friend",
      wins: r.wins || 0,
      losses: r.losses || 0,
      draws: r.draws || 0,
      last_played_at: r.last_played_at || null,
      they_owe_me: { count: 0, stakes: [] },
      i_owe_them: { count: 0, stakes: [] },
    });
  }
  // Merge IOU groupings
  for (const [otherId, bucket] of Object.entries(iou.owed_to_me || {})) {
    const e = map.get(otherId) || newEntry(otherId, bucket.name);
    e.they_owe_me = { count: bucket.count, stakes: bucket.stakes || [] };
    e.name = e.name || bucket.name;
    map.set(otherId, e);
  }
  for (const [otherId, bucket] of Object.entries(iou.i_owe || {})) {
    const e = map.get(otherId) || newEntry(otherId, bucket.name);
    e.i_owe_them = { count: bucket.count, stakes: bucket.stakes || [] };
    e.name = e.name || bucket.name;
    map.set(otherId, e);
  }
  // Sort: most active rivals first (games + iou count)
  return Array.from(map.values()).sort((a, b) => {
    const aTotal = (a.wins + a.losses + a.draws) * 2 + a.they_owe_me.count + a.i_owe_them.count;
    const bTotal = (b.wins + b.losses + b.draws) * 2 + b.they_owe_me.count + b.i_owe_them.count;
    return bTotal - aTotal;
  }).slice(0, 6);
}

function newEntry(user_id, name) {
  return {
    user_id, name: name || "Friend",
    wins: 0, losses: 0, draws: 0, last_played_at: null,
    they_owe_me: { count: 0, stakes: [] },
    i_owe_them: { count: 0, stakes: [] },
  };
}

function tallyEmojis(stakes) {
  // Group identical stake_label strings → "🍦 Ice cream ×2".
  const counts = {};
  for (const s of stakes) {
    counts[s] = (counts[s] || 0) + 1;
  }
  return Object.entries(counts)
    .map(([label, n]) => ({
      // Pull the leading emoji if present, else use 📝
      emoji: extractLeadingEmoji(label) || "📝",
      label,
      n,
    }));
}

function extractLeadingEmoji(s) {
  if (!s) return null;
  // Match the first grapheme — most preset stakes start with an emoji
  const m = s.match(/^(\p{Extended_Pictographic}[\u{FE00}-\u{FE0F}\u{1F3FB}-\u{1F3FF}\u{200D}\p{Extended_Pictographic}]*)/u);
  return m ? m[1] : null;
}

function relTimeShort(iso) {
  if (!iso) return "";
  const d = new Date(iso);
  const diff = Math.max(0, Date.now() - d.getTime());
  const min = Math.floor(diff / 60000);
  if (min < 1) return "just now";
  if (min < 60) return `${min}m`;
  const hr = Math.floor(min / 60);
  if (hr < 24) return `${hr}h`;
  const day = Math.floor(hr / 24);
  if (day < 30) return `${day}d`;
  return d.toLocaleDateString();
}

function RivalCard({ entry, index }) {
  const games = entry.wins + entry.losses + entry.draws;
  const winRate = games > 0 ? Math.round((entry.wins / games) * 100) : null;
  const ringColor = colorFromName(entry.name);
  const theyOweTally = tallyEmojis(entry.they_owe_me.stakes);
  const iOweTally = tallyEmojis(entry.i_owe_them.stakes);

  return (
    <motion.div
      initial={{ opacity: 0, y: 6 }} animate={{ opacity: 1, y: 0 }}
      transition={{ delay: index * 0.03 }}
      className="glass rounded-2xl p-4"
      style={{ borderLeft: `3px solid ${ringColor}` }}
      data-testid={`rival-card-${index}`}
    >
      <div className="flex items-center gap-3 mb-3">
        <div
          className="rounded-full flex items-center justify-center font-display font-black flex-shrink-0"
          style={{
            width: 40, height: 40,
            background: `${ringColor}22`,
            color: ringColor,
            border: `1.5px solid ${ringColor}`,
            fontSize: 13, letterSpacing: 0.5,
          }}
          data-testid={`rival-avatar-${index}`}
        >{getInitials(entry.name)}</div>
        <div className="flex-1 min-w-0">
          <div className="font-display font-bold text-sm truncate" data-testid={`rival-name-${index}`}>
            {entry.name}
          </div>
          {games > 0 ? (
            <div className="text-xs text-white/55 mt-0.5" data-testid={`rival-record-${index}`}>
              <b style={{ color: "#3DDC97" }}>{entry.wins}W</b>
              <span className="text-white/30 mx-1">·</span>
              <b style={{ color: "#FF8A8A" }}>{entry.losses}L</b>
              {entry.draws > 0 && (
                <>
                  <span className="text-white/30 mx-1">·</span>
                  <b className="text-white/50">{entry.draws}D</b>
                </>
              )}
              {winRate != null && (
                <span className="text-white/40 ml-2">{winRate}%</span>
              )}
              {entry.last_played_at && (
                <span className="text-white/30 ml-2">· {relTimeShort(entry.last_played_at)}</span>
              )}
            </div>
          ) : (
            <div className="text-xs text-white/45 mt-0.5">No casual matches yet</div>
          )}
        </div>
      </div>

      {/* IOU tally rows */}
      {(theyOweTally.length > 0 || iOweTally.length > 0) && (
        <div className="space-y-1.5">
          {theyOweTally.length > 0 && (
            <div
              className="rounded-xl px-3 py-1.5 flex items-center gap-2 flex-wrap"
              style={{
                background: "rgba(61,220,151,0.08)",
                border: "1px solid rgba(61,220,151,0.30)",
              }}
              data-testid={`rival-iou-they-owe-${index}`}
            >
              <span className="label-eyebrow" style={{ color: "#3DDC97", fontSize: 9 }}>
                THEY OWE YOU
              </span>
              {theyOweTally.map((t, i) => (
                <span key={i} className="text-sm" title={t.label}>
                  {t.emoji}{t.n > 1 && <span className="font-mono font-bold ml-0.5" style={{ fontSize: 11 }}>×{t.n}</span>}
                </span>
              ))}
            </div>
          )}
          {iOweTally.length > 0 && (
            <div
              className="rounded-xl px-3 py-1.5 flex items-center gap-2 flex-wrap"
              style={{
                background: "rgba(255,215,0,0.06)",
                border: "1px solid rgba(255,215,0,0.25)",
              }}
              data-testid={`rival-iou-i-owe-${index}`}
            >
              <span className="label-eyebrow" style={{ color: "#FFD700", fontSize: 9 }}>
                YOU OWE
              </span>
              {iOweTally.map((t, i) => (
                <span key={i} className="text-sm" title={t.label}>
                  {t.emoji}{t.n > 1 && <span className="font-mono font-bold ml-0.5" style={{ fontSize: 11 }}>×{t.n}</span>}
                </span>
              ))}
            </div>
          )}
        </div>
      )}
    </motion.div>
  );
}
